import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { customersAPI } from '../lib/api';
import { Users, Search, Phone, Mail, DollarSign, ShoppingCart, Wallet, TrendingUp, User, X } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface Customer {
    _id: string;
    name: string;
    email: string;
    phone?: string;
    role: string;
    createdAt: string;
    stats?: {
        totalOrders: number;
        totalSpent: number;
        walletBalance: number;
    };
}

interface CustomerStats {
    totalCustomers: number;
    activeCustomers: number;
    newCustomersThisMonth: number;
    totalRevenue: number;
    averageOrderValue: number;
    topCustomers: Array<{
        _id: string;
        orderCount: number;
        totalSpent: number;
        user?: {
            name: string;
            email: string;
            phone?: string;
        };
    }>;
}

const Customers = () => {
    const navigate = useNavigate();
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [stats, setStats] = useState<CustomerStats>({
        totalCustomers: 0,
        activeCustomers: 0,
        newCustomersThisMonth: 0,
        totalRevenue: 0,
        averageOrderValue: 0,
        topCustomers: [],
    });
    const [isAdmin, setIsAdmin] = useState(false);
    const [isCustomer, setIsCustomer] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [showDetails, setShowDetails] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/signup');
            return;
        }

        // Check user role
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                const user = JSON.parse(userData);
                const adminRoles = ['admin', 'super_user', 'entity_admin'];
                setIsAdmin(adminRoles.includes(user.role));
                setIsCustomer(user.role === 'customer');
            } catch (e) {
                console.error('Error parsing user data:', e);
            }
        }

        fetchData();
    }, []);

    useEffect(() => {
        filterCustomers();
    }, [customers, searchTerm]);

    const fetchData = async () => {
        try {
            const [customersRes, statsRes] = await Promise.all([
                customersAPI.getAll({ limit: 100 }).catch((err: any) => {
                    if (err.response?.status === 403) {
                        toast.error('Access denied');
                    }
                    return { data: { customers: [] } };
                }),
                customersAPI.getStats().catch(() => {
                    // Silently fail for stats if not admin
                    return { data: {} as CustomerStats };
                }),
            ]);

            setCustomers(customersRes.data?.customers || customersRes.data || []);

            // Merge stats with defaults to prevent undefined errors
            const statsData = statsRes.data || {};
            setStats({
                totalCustomers: statsData.totalCustomers || 0,
                activeCustomers: statsData.activeCustomers || 0,
                newCustomersThisMonth: statsData.newCustomersThisMonth || 0,
                totalRevenue: statsData.totalRevenue || 0,
                averageOrderValue: statsData.averageOrderValue || 0,
                topCustomers: statsData.topCustomers || [],
            });
        } catch (error: any) {
            console.error('Error fetching customers:', error);
            if (error.response?.status !== 403) {
                toast.error(error.response?.data?.message || 'Failed to load data');
            }
        } finally {
            setLoading(false);
        }
    };

    const filterCustomers = () => {
        if (!searchTerm) {
            setFilteredCustomers(customers);
            return;
        }

        const filtered = customers.filter((customer) =>
            customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            customer.phone?.includes(searchTerm)
        );

        setFilteredCustomers(filtered);
    };

    const handleViewDetails = async (customer: Customer) => {
        try {
            const response = await customersAPI.getOne(customer._id);
            setSelectedCustomer(response.data);
            setShowDetails(true);
        } catch (error: any) {
            toast.error('Failed to load customer details');
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900">{isCustomer ? 'My Profile' : 'Customers'}</h1>
                <p className="text-gray-500">{isCustomer ? 'View your account information' : 'Manage your customer base and view their activity'}</p>
            </div>

            {/* Stats Cards - Only show for admins */}
            {isAdmin && (
                <div className="responsive-stats-grid mb-8">
                    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
                                <Users className="text-blue-600" size={20} />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{stats.totalCustomers}</p>
                                <p className="text-xs sm:text-sm text-gray-500 truncate">Total Customers</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center shrink-0">
                                <TrendingUp className="text-green-600" size={20} />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{stats.activeCustomers}</p>
                                <p className="text-xs sm:text-sm text-gray-500 truncate">Active Customers</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center shrink-0">
                                <User className="text-purple-600" size={20} />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{stats.newCustomersThisMonth}</p>
                                <p className="text-xs sm:text-sm text-gray-500 truncate">New This Month</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center shrink-0">
                                <DollarSign className="text-orange-600" size={20} />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">₹{(stats.totalRevenue || 0).toLocaleString()}</p>
                                <p className="text-xs sm:text-sm text-gray-500 truncate">Total Revenue</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Top Customers - Only show for admins */}
            {isAdmin && stats.topCustomers && stats.topCustomers.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8">
                    <h3 className="font-bold text-gray-900 mb-4">Top Customers</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {stats.topCustomers.slice(0, 6).map((topCustomer, index) => (
                            <div key={topCustomer._id || index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold shrink-0">
                                        {index + 1}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-bold text-gray-900 truncate">{topCustomer.user?.name || 'Unknown'}</p>
                                        <p className="text-xs text-gray-500">{topCustomer.orderCount || 0} orders</p>
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="font-bold text-[#10b981]">₹{(topCustomer.totalSpent || 0).toLocaleString()}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Search - Only show for admins */}
            {isAdmin && (
                <div className="mb-8">
                    <div className="relative max-w-2xl">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Search by name, email, or phone..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#10b981] focus:border-transparent outline-none transition-all shadow-sm"
                        />
                    </div>
                </div>
            )}

            {/* Info Message */}
            {isCustomer && (
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-8 flex items-center gap-3 text-blue-800">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
                        <User size={20} />
                    </div>
                    <p className="text-sm font-medium">
                        <strong>My Profile:</strong> View your account information and activity.
                    </p>
                </div>
            )}

            {/* Customers List */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="font-bold text-gray-900">
                        {isCustomer ? 'My Profile' : `All Customers`} <span className="text-gray-400 font-medium ml-1">({filteredCustomers.length})</span>
                    </h2>
                </div>
                <div className="divide-y divide-gray-100">
                    {filteredCustomers.length > 0 ? (
                        filteredCustomers.map((customer) => (
                            <div
                                key={customer._id}
                                className="p-4 sm:p-6 hover:bg-gray-50 transition-colors cursor-pointer group"
                                onClick={() => handleViewDetails(customer)}
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                                            <User className="text-green-600" size={24} />
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="font-bold text-gray-900 truncate group-hover:text-[#10b981] transition-colors">{customer.name}</h3>
                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                                                {customer.email && (
                                                    <div className="flex items-center gap-1 text-xs sm:text-sm text-gray-500">
                                                        <Mail size={14} />
                                                        <span className="truncate max-w-[150px] sm:max-w-none">{customer.email}</span>
                                                    </div>
                                                )}
                                                {customer.phone && (
                                                    <div className="flex items-center gap-1 text-xs sm:text-sm text-gray-500">
                                                        <Phone size={14} />
                                                        <span>{customer.phone}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between sm:justify-end gap-6">
                                        {customer.stats && (
                                            <div className="flex gap-4 sm:gap-8">
                                                <div className="text-right">
                                                    <p className="text-xs sm:text-sm font-bold text-gray-900">
                                                        {customer.stats.totalOrders || 0} orders
                                                    </p>
                                                    <p className="text-[10px] sm:text-xs text-gray-500 font-medium tracking-wide">₹{(customer.stats.totalSpent || 0).toLocaleString()}</p>
                                                </div>
                                                <div className="text-right hide-on-mobile">
                                                    <p className="text-sm font-bold text-gray-900">Wallet</p>
                                                    <p className="text-xs text-gray-500 font-medium tracking-wide">₹{(customer.stats.walletBalance || 0).toLocaleString()}</p>
                                                </div>
                                            </div>
                                        )}
                                        <button className="px-4 py-2 bg-green-50 text-green-600 rounded-xl font-bold text-sm hover:bg-green-600 hover:text-white transition-all">
                                            View
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-20">
                            <Users className="mx-auto text-gray-200 mb-4" size={80} />
                            <h3 className="text-lg font-bold text-gray-900">No data found</h3>
                            <p className="text-gray-500">{isCustomer ? 'Unable to load your profile' : 'Try adjusting your search terms'}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Customer Details Modal */}
            {showDetails && selectedCustomer && (
                <div className="modal-overlay backdrop-blur-sm">
                    <div className="modal-content max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-100 sticky top-0 bg-white z-10 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-900">{isCustomer ? 'My Profile' : 'Customer Details'}</h2>
                            <button
                                onClick={() => setShowDetails(false)}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6 space-y-8">
                            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                                    <User size={32} />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">{selectedCustomer.name}</h3>
                                    <p className="text-gray-500 font-medium">Member since {new Date(selectedCustomer.createdAt).toLocaleDateString()}</p>
                                </div>
                            </div>

                            <div>
                                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Contact Information</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <div className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl">
                                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 shrink-0">
                                            <Mail size={20} />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs text-gray-500 font-bold uppercase">Email</p>
                                            <p className="font-bold text-gray-900 truncate">{selectedCustomer.email}</p>
                                        </div>
                                    </div>
                                    {selectedCustomer.phone && (
                                        <div className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl">
                                            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-green-600 shrink-0">
                                                <Phone size={20} />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-xs text-gray-500 font-bold uppercase">Phone</p>
                                                <p className="font-bold text-gray-900 truncate">{selectedCustomer.phone}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {selectedCustomer.stats && (
                                <div>
                                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Account Statistics</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <div className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100">
                                            <div className="flex items-center gap-2 mb-2">
                                                <ShoppingCart className="text-blue-600" size={18} />
                                                <p className="text-xs font-bold text-blue-600 uppercase">Orders</p>
                                            </div>
                                            <p className="text-2xl font-bold text-gray-900">{selectedCustomer.stats.totalOrders || 0}</p>
                                        </div>
                                        <div className="bg-green-50/50 p-4 rounded-2xl border border-green-100">
                                            <div className="flex items-center gap-2 mb-2">
                                                <DollarSign className="text-green-600" size={18} />
                                                <p className="text-xs font-bold text-green-600 uppercase">Spent</p>
                                            </div>
                                            <p className="text-2xl font-bold text-gray-900">
                                                ₹{(selectedCustomer.stats.totalSpent || 0).toLocaleString()}
                                            </p>
                                        </div>
                                        <div className="bg-purple-50/50 p-4 rounded-2xl border border-purple-100">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Wallet className="text-purple-600" size={18} />
                                                <p className="text-xs font-bold text-purple-600 uppercase">Wallet</p>
                                            </div>
                                            <p className="text-2xl font-bold text-gray-900">
                                                ₹{(selectedCustomer.stats.walletBalance || 0).toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="p-6 border-t border-gray-100 bg-gray-50 flex gap-4">
                            <button
                                onClick={() => setShowDetails(false)}
                                className="flex-1 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-100 transition-colors"
                            >
                                Close
                            </button>
                            {!isCustomer && (
                                <button
                                    className="flex-1 py-3 bg-[#10b981] text-white rounded-xl font-bold hover:bg-[#059669] transition-colors shadow-lg shadow-[#10b981]/20"
                                >
                                    Send Message
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default Customers;